import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '@/hooks/useApi';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { ResultRow } from '@/components/customers/ResultRow';
import type { CustomerListItem, ExternalMatch } from '@/api/customers';
import { useUserRole } from '@/hooks/useUserRole';
import axios from 'axios';
import '@/styles/screens.css';

type Status = 'idle' | 'loading' | 'ready' | 'error';

/** Look up customer (SPEC §7): debounced search with loading/empty/error states.
 *  Identifier queries (email/phone) also check the Shopify/BC mirrors — people
 *  who exist but aren't in the caller's list yet get an "Add & link" action. */
export function LookupPage() {
  const api = useApi();
  const navigate = useNavigate();
  const { stores } = useUserRole();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [results, setResults] = useState<CustomerListItem[]>([]);
  const [external, setExternal] = useState<ExternalMatch[]>([]);
  const [adopting, setAdopting] = useState<string | null>(null);
  const debouncedQ = useDebouncedValue(q, 300);

  useEffect(() => {
    const term = debouncedQ.trim();
    if (term.length < 2) {
      setStatus('idle');
      setResults([]);
      setExternal([]);
      return;
    }

    const controller = new AbortController();
    setStatus('loading');
    api
      .search(term, controller.signal)
      .then(({ results: rows, external: ext }) => {
        setResults(rows);
        setExternal(ext);
        setStatus('ready');
      })
      .catch((err) => {
        if (axios.isCancel(err) || controller.signal.aborted) return;
        setStatus('error');
      });
    return () => controller.abort();
  }, [debouncedQ, api]);

  async function adopt(m: ExternalMatch) {
    const key = m.shopify_gid ?? m.bc_contact_no ?? '';
    setAdopting(key);
    try {
      const created = await api.create({
        firstName: m.first_name ?? undefined,
        lastName: m.last_name ?? undefined,
        email: m.email ?? undefined,
        phone: m.phone ?? undefined,
        homeStoreId: stores[0]?.store_id,
        link: {
          shopifyGid: m.shopify_gid ?? undefined,
          bcContactNo: m.bc_contact_no ?? undefined,
        },
      });
      if (created.ok && created.customer) navigate(`/customers/${created.customer.customer_id}`);
    } finally {
      setAdopting(null);
    }
  }

  return (
    <div className="screen">
      <p className="screen-greeting">Customers</p>
      <h1 className="screen-title">Look up a customer</h1>

      <div className="search-field">
        <i className="ti ti-search search-icon" aria-hidden="true" />
        <input
          autoFocus
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name, phone or email"
          aria-label="Search customers"
        />
      </div>

      {status === 'idle' && (
        <p className="state-msg">Start typing to search your store&rsquo;s customers.</p>
      )}

      {status === 'loading' && (
        <div className="result-list" aria-hidden="true">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="skeleton-row" />
          ))}
        </div>
      )}

      {status === 'error' && (
        <p className="state-msg state-msg--error">
          Something went wrong loading results. Please try again.
        </p>
      )}

      {status === 'ready' && results.length === 0 && external.length === 0 && (
        <p className="state-msg">No customers match.</p>
      )}

      {status === 'ready' && results.length > 0 && (
        <div className="result-list">
          {results.map((c) => (
            <ResultRow
              key={c.customer_id}
              customer={c}
              onOpen={(id) => navigate(`/customers/${id}`)}
            />
          ))}
        </div>
      )}

      {status === 'ready' && external.length > 0 && (
        <>
          <p className="section-title" style={{ marginTop: 20 }}>
            Found elsewhere — not in your list yet
          </p>
          <div className="result-list">
            {external.map((m) => {
              const key = m.shopify_gid ?? m.bc_contact_no ?? '';
              return (
                <div key={key} className="result-row" style={{ cursor: 'default' }}>
                  <span>
                    <span className="result-row-name">
                      {[m.first_name, m.last_name].filter(Boolean).join(' ') || m.email || '—'}
                      <span className={`row-badge ${m.source === 'shopify' ? 'row-badge--web' : 'row-badge--wholesale'}`}>
                        {m.source === 'shopify' ? 'Web' : 'BC member'}
                      </span>
                    </span>
                    <span className="result-row-meta" style={{ display: 'block' }}>
                      {[m.email, m.phone].filter(Boolean).join(' · ')}
                    </span>
                  </span>
                  <button
                    type="button"
                    className="btn btn--sm"
                    disabled={adopting === key}
                    onClick={() => adopt(m)}
                  >
                    {adopting === key ? 'Adding…' : 'Add & link'}
                  </button>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
